import { partnerLinkIdPattern } from "~/features/partner/link-id";

/**
 * Allowlist of paths that are safe to redirect to after authentication.
 * Each entry can be:
 * - A RegExp for pattern matching (e.g., /patient/<id>)
 * - A string for exact path matching
 */
const returnToAllowed = [
  // Pattern: /patient/<id> - matches patient detail pages
  /^\/patient\/[a-zA-Z0-9]{10,30}\/?$/,

  // Exact match: /plans - pricing/subscription page
  /^\/plans\/?$/,

  // Pattern: /connect or /connect/<sub-path> - OAuth-style consent page
  // for Optogrid Connect integrations. Round-trip required so users sent
  // through /access?return-to=/connect?... land back on the consent
  // screen with their original query params intact.
  /^\/connect(\/.*)?$/,

  // Pattern: /link-c/<id> - partner invite confirmation. A signed-out
  // visitor signs in and lands back on the same confirmation.
  new RegExp(`^/link-c/${partnerLinkIdPattern}/?$`),

  // Add more patterns/paths as needed:
  // /^\/settings\/?$/,
  // /^\/dashboard\/?$/,
];

/**
 * Extracts pathname from a full URL, stripping hash fragments.
 * Preserves query parameters.
 *
 * @param url - Full URL string (e.g., "/plans?plan=pro#section")
 * @returns Pathname with query params, without hash (e.g., "/plans?plan=pro")
 */
export const stripHashFromUrl = (url: string): string => {
  // Remove hash fragment if present
  const withoutHash = url.split("#")[0];
  return withoutHash || "/";
};

/**
 * Extracts the pathname (without query params or hash) from a URL.
 *
 * @param url - Full URL string (e.g., "/plans?plan=pro#section")
 * @returns Just the pathname (e.g., "/plans")
 */
export const extractPathname = (url: string): string => {
  const withoutHash = stripHashFromUrl(url);
  const pathname = withoutHash.split("?")[0];
  return pathname || "/";
};

/**
 * Checks if a given path (without query params/hash) is in the allowlist.
 *
 * @param pathname - The pathname to check (e.g., "/plans")
 * @returns true if the path is allowed for return-to redirects
 */
export const isPathnameAllowed = (pathname: string): boolean => {
  if (!pathname) {
    return false;
  }
  return returnToAllowed.some((allowed) => allowed.test(pathname));
};

/**
 * Checks if a full URL (with query params) can be used as a return-to target.
 * Strips hash fragments and validates the pathname against the allowlist.
 *
 * @param url - Full URL string (e.g., "/plans?plan=pro#section")
 * @returns true if the URL is safe to redirect to after authentication
 */
export const canReturnTo = (url: string | null | undefined): boolean => {
  if (!url) {
    return false;
  }

  const pathname = extractPathname(url);
  return isPathnameAllowed(pathname);
};

/**
 * Sanitizes a return URL by validating it against the allowlist.
 * Strips hash fragments and preserves query parameters.
 *
 * @param url - The URL to sanitize (e.g., "/plans?plan=pro#section")
 * @param retDefault - Default URL to return if validation fails
 * @returns Sanitized URL with query params but without hash, or default if not allowed
 */
export const sanitizeReturnPath = (
  url: string | null | undefined,
  retDefault: string,
): string => {
  if (!url) {
    return retDefault;
  }

  // Strip hash but keep query params
  const urlWithoutHash = stripHashFromUrl(url);

  // Validate pathname
  const pathname = extractPathname(urlWithoutHash);
  if (!isPathnameAllowed(pathname)) {
    return retDefault;
  }

  return urlWithoutHash;
};
